import { useCallback, useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { Input, Row, Col } from "antd";

import "leaflet/dist/leaflet.css";

const defaultCenter = [28.6139, 77.209]; // India [lat, lng]
const NOMINATIM_URL = "https://nominatim.openstreetmap.org";
const MAP_STYLE = { height: 240, borderRadius: 8 };

// Fix default marker icon in react-leaflet (webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

function MapClickHandler({ onMapClick }) {
  useMapEvents({ click: onMapClick });
  return null;
}

function MapCenterUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] != null && center[1] != null) {
      map.flyTo(center, zoom ?? 15);
    }
  }, [center, zoom, map]);
  return null;
}

function LocationMapPicker({ value = {}, onChange }) {
  const { location = "", latitude, longitude } = value;
  const [center, setCenter] = useState(
    latitude != null && longitude != null ? [Number(latitude), Number(longitude)] : defaultCenter
  );
  const [markerPosition, setMarkerPosition] = useState(
    latitude != null && longitude != null ? [Number(latitude), Number(longitude)] : null
  );
  const [addressInputValue, setAddressInputValue] = useState(location || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (latitude != null && longitude != null) {
      const pos = [Number(latitude), Number(longitude)];
      setMarkerPosition(pos);
      setCenter(pos);
    } else {
      setMarkerPosition(null);
      setCenter(defaultCenter);
    }
  }, [latitude, longitude]);

  useEffect(() => {
    setAddressInputValue(location || "");
  }, [location]);

  const notifyChange = useCallback(
    (updates) => {
      if (onChange) onChange({ ...value, ...updates });
    },
    [onChange, value]
  );

  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const res = await fetch(
        `${NOMINATIM_URL}/reverse?lat=${lat}&lon=${lng}&format=json`,
        { headers: { "Accept-Language": "en", "User-Agent": "ZipptonApp/1.0" } }
      );
      const data = await res.json();
      return data?.display_name || "";
    } catch (e) {
      return "";
    }
  }, []);

  const searchAddress = useCallback(async (q) => {
    const query = (q || "").trim();
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(
        `${NOMINATIM_URL}/search?q=${encodeURIComponent(query)}&format=json&limit=5`,
        { headers: { "Accept-Language": "en", "User-Agent": "ZipptonApp/1.0" } }
      );
      const data = await res.json();
      setSearchResults(Array.isArray(data) ? data : []);
    } catch (e) {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(() => searchAddress(searchQuery), 400);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, searchAddress]);

  const handleMapClick = useCallback(
    async (e) => {
      const { lat, lng } = e.latlng;
      setMarkerPosition([lat, lng]);
      setCenter([lat, lng]);
      setReverseLoading(true);
      const address = await reverseGeocode(lat, lng);
      setReverseLoading(false);
      setAddressInputValue(address);
      notifyChange({ latitude: lat, longitude: lng, location: address || location });
    },
    [notifyChange, reverseGeocode, location]
  );

  const handleSelectResult = useCallback(
    (item) => {
      const lat = parseFloat(item.lat);
      const lng = parseFloat(item.lon);
      const addr = item.display_name || "";
      setMarkerPosition([lat, lng]);
      setCenter([lat, lng]);
      setAddressInputValue(addr);
      setSearchQuery("");
      setSearchResults([]);
      notifyChange({ location: addr, latitude: lat, longitude: lng });
    },
    [notifyChange]
  );

  const handleAddressInputChange = (e) => {
    const v = e.target.value;
    setAddressInputValue(v);
    setSearchQuery(v);
    notifyChange({ location: v });
  };

  const handleCoordinateChange = (field) => (e) => {
    const rawValue = e.target.value;
    const coordinate = rawValue === "" ? undefined : Number(rawValue);
    if (coordinate !== undefined && !Number.isFinite(coordinate)) return;
    notifyChange({ [field]: coordinate });
  };

  return (
    <div className="location-map-picker">
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>
            Search address
          </label>
          <input
            type="text"
            placeholder="Type city or address (e.g. Delhi, Connaught Place)"
            value={addressInputValue}
            onChange={handleAddressInputChange}
            onFocus={() => searchQuery.trim().length >= 2 && searchAddress(searchQuery)}
            disabled={reverseLoading}
            autoComplete="off"
            style={{
              width: "100%",
              padding: "6px 11px",
              fontSize: 14,
              border: "1px solid #d9d9d9",
              borderRadius: 6,
              outline: "none",
            }}
          />
          {searchResults.length > 0 && (
            <ul
              style={{
                margin: "4px 0 0",
                padding: 0,
                listStyle: "none",
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                background: "#fff",
                maxHeight: 200,
                overflow: "auto",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                zIndex: 1000,
                position: "relative",
              }}
            >
              {searchResults.map((item, i) => (
                <li
                  key={i}
                  onClick={() => handleSelectResult(item)}
                  style={{
                    padding: "8px 11px",
                    cursor: "pointer",
                    borderBottom: i < searchResults.length - 1 ? "1px solid #f0f0f0" : "none",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#f5f5f5";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#fff";
                  }}
                >
                  {item.display_name}
                </li>
              ))}
            </ul>
          )}
          {searching && (
            <div style={{ marginTop: 4, fontSize: 12, color: "#8c8c8c" }}>Searching…</div>
          )}
          {!searching && addressInputValue && searchResults.length === 0 && searchQuery.trim().length >= 2 && (
            <div style={{ marginTop: 4, fontSize: 12, color: "#8c8c8c" }}>No results. Try different words or pick on map.</div>
          )}
          {!searching && !addressInputValue && (
            <div style={{ marginTop: 4, fontSize: 12, color: "#8c8c8c" }}>
              Type to search, then select from list — or click on the map below.
            </div>
          )}
        </Col>
        <Col span={24}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>
            Or pick on map
          </label>
          <div style={MAP_STYLE}>
            <MapContainer
              center={center}
              zoom={markerPosition ? 15 : 5}
              style={{ height: "100%", width: "100%", borderRadius: 8 }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapCenterUpdater center={center} zoom={markerPosition ? 15 : 5} />
              <MapClickHandler onMapClick={handleMapClick} />
              {markerPosition && <Marker position={markerPosition} />}
            </MapContainer>
          </div>
        </Col>
        <Col span={12}>
          <Input
            placeholder="Latitude"
            value={latitude != null ? String(latitude) : ""}
            type="number"
            step="any"
            min={-90}
            max={90}
            onChange={handleCoordinateChange("latitude")}
            size="small"
          />
        </Col>
        <Col span={12}>
          <Input
            placeholder="Longitude"
            value={longitude != null ? String(longitude) : ""}
            type="number"
            step="any"
            min={-180}
            max={180}
            onChange={handleCoordinateChange("longitude")}
            size="small"
          />
        </Col>
      </Row>
    </div>
  );
}

export default LocationMapPicker;
